/**
 * Phase 4c — ParetoFrontierChart
 *
 * Recharts ScatterChart over (design_cost, pk) with the Pareto
 * frontier highlighted, the knee point pinned with a diamond, and
 * the baseline marked with a star at (0, baseline_pk).
 *
 * Empty state when no data is available.
 *
 * Props:
 *   candidates       — array of optimisation candidate dicts
 *                      (carry pk, design_cost, pareto_flag, etc.)
 *   kneeCandidateId  — id of the knee candidate (string|null)
 *   baselinePk       — number|null
 *   bestCandidateId  — id of the recommended candidate (optional)
 */
import { useMemo } from "react";
import {
    CartesianGrid,
    Legend,
    ResponsiveContainer,
    Scatter,
    ScatterChart,
    Tooltip,
    XAxis,
    YAxis,
    ZAxis,
} from "recharts";

function num(v, digits = 3) {
    if (typeof v !== "number" || Number.isNaN(v)) return "—";
    return v.toFixed(digits);
}

function PointTooltip({ active, payload }) {
    if (!active || !payload || !payload.length) return null;
    const p = payload[0]?.payload || {};
    return (
        <div
            className="rounded-md border border-ux-border-1 bg-ux-bg-2 px-3 py-2 text-[11px] text-ux-text-2 shadow"
            data-testid="pareto-chart-tooltip"
        >
            <div className="font-mono text-ux-text-3">
                Candidate #{p.candidate_index ?? "—"}
            </div>
            <div className="mt-1 grid grid-cols-2 gap-x-3 gap-y-0.5">
                <span className="text-ux-text-3">Pk</span>
                <span className="tabular-nums text-ux-text-1">
                    {num(p.pk, 3)}
                    {typeof p.pk_se === "number" && p.pk_se > 0
                        ? ` ± ${num(p.pk_se, 3)}`
                        : ""}
                </span>
                <span className="text-ux-text-3">Design cost</span>
                <span className="tabular-nums text-ux-text-1">
                    {num(p.design_cost, 3)}
                </span>
                {typeof p.recommendation_score === "number" && (
                    <>
                        <span className="text-ux-text-3">Score</span>
                        <span className="tabular-nums text-ux-text-1">
                            {num(p.recommendation_score, 3)}
                        </span>
                    </>
                )}
                <span className="text-ux-text-3">Pareto</span>
                <span
                    className={
                        p.pareto_flag ? "text-ux-accent" : "text-ux-text-3"
                    }
                >
                    {p.pareto_flag ? "yes" : "no"}
                </span>
                {p.is_knee && (
                    <>
                        <span className="text-ux-text-3">Knee</span>
                        <span className="text-ux-accent">elected</span>
                    </>
                )}
            </div>
        </div>
    );
}

export default function ParetoFrontierChart({
    candidates,
    kneeCandidateId = null,
    baselinePk = null,
    bestCandidateId = null,
}) {
    const data = useMemo(() => {
        if (!Array.isArray(candidates)) return { all: [], frontier: [], knee: [], baseline: [] };
        const cleaned = candidates
            .filter(
                (c) =>
                    typeof c.pk === "number"
                    && typeof c.design_cost === "number"
                    && Number.isFinite(c.pk)
                    && Number.isFinite(c.design_cost),
            )
            .map((c) => ({
                ...c,
                is_knee: kneeCandidateId && c.id === kneeCandidateId,
                is_best: bestCandidateId && c.id === bestCandidateId,
            }));
        const all = cleaned.filter((c) => !c.pareto_flag);
        const frontier = cleaned
            .filter((c) => c.pareto_flag && !(kneeCandidateId && c.id === kneeCandidateId))
            .sort((a, b) => (a.design_cost || 0) - (b.design_cost || 0));
        const knee = cleaned.filter(
            (c) => kneeCandidateId && c.id === kneeCandidateId,
        );
        const baseline = typeof baselinePk === "number"
            ? [{ design_cost: 0, pk: baselinePk, candidate_index: "baseline" }]
            : [];
        return { all, frontier, knee, baseline };
    }, [candidates, kneeCandidateId, baselinePk, bestCandidateId]);

    const hasAny =
        data.all.length + data.frontier.length + data.knee.length > 0;

    return (
        <div
            className="rounded-md border border-ux-border-1 bg-ux-bg-1 p-4 space-y-3"
            data-testid="pareto-frontier-chart"
        >
            <div className="flex items-baseline justify-between gap-2 flex-wrap">
                <h3
                    className="text-[13px] text-ux-text-1 font-medium"
                    data-testid="pareto-frontier-title"
                >
                    Performance vs design cost
                </h3>
                <span className="text-[10px] text-ux-text-3 uppercase tracking-wider">
                    Pareto frontier
                </span>
            </div>

            {!hasAny ? (
                <div
                    className="py-12 text-center text-[12px] text-ux-text-3 italic"
                    data-testid="pareto-frontier-empty"
                >
                    No Pareto frontier produced for this study yet.
                </div>
            ) : (
                <>
                    <div
                        style={{ width: "100%", height: 320 }}
                        data-testid="pareto-frontier-canvas"
                    >
                        <ResponsiveContainer width="100%" height="100%">
                            <ScatterChart
                                margin={{ top: 16, right: 24, bottom: 16, left: 8 }}
                            >
                                <CartesianGrid
                                    strokeDasharray="3 3"
                                    stroke="var(--ux-border-1, #2b3447)"
                                />
                                <XAxis
                                    type="number"
                                    dataKey="design_cost"
                                    name="Design cost"
                                    tick={{ fontSize: 10, fill: "var(--ux-text-3)" }}
                                    label={{
                                        value: "Design cost (lower is better)",
                                        position: "insideBottom",
                                        offset: -8,
                                        fill: "var(--ux-text-3)",
                                        fontSize: 10,
                                    }}
                                />
                                <YAxis
                                    type="number"
                                    dataKey="pk"
                                    name="Pk"
                                    domain={[0, 1]}
                                    tick={{ fontSize: 10, fill: "var(--ux-text-3)" }}
                                    label={{
                                        value: "Pk",
                                        angle: -90,
                                        position: "insideLeft",
                                        fill: "var(--ux-text-3)",
                                        fontSize: 10,
                                    }}
                                />
                                <ZAxis range={[40, 80]} />
                                <Tooltip
                                    cursor={{ stroke: "var(--ux-text-3)", strokeWidth: 0.5 }}
                                    content={<PointTooltip />}
                                />
                                <Legend
                                    iconSize={8}
                                    wrapperStyle={{
                                        fontSize: 10,
                                        color: "var(--ux-text-3)",
                                    }}
                                />
                                <Scatter
                                    name="Candidates"
                                    data={data.all}
                                    fill="var(--ux-text-3, #5b6678)"
                                    fillOpacity={0.65}
                                    isAnimationActive={false}
                                />
                                <Scatter
                                    name="Pareto frontier"
                                    data={data.frontier}
                                    fill="var(--ux-accent, #3fc1c9)"
                                    line={{
                                        stroke: "var(--ux-accent, #3fc1c9)",
                                        strokeWidth: 1.2,
                                        strokeOpacity: 0.7,
                                    }}
                                    lineType="joint"
                                    shape="circle"
                                    isAnimationActive={false}
                                />
                                <Scatter
                                    name="Knee"
                                    data={data.knee}
                                    fill="var(--ux-text-1, #e8e2d2)"
                                    stroke="var(--ux-accent, #3fc1c9)"
                                    shape="diamond"
                                    isAnimationActive={false}
                                />
                                <Scatter
                                    name="Baseline"
                                    data={data.baseline}
                                    fill="var(--ux-text-1, #e8e2d2)"
                                    stroke="var(--ux-accent, #3fc1c9)"
                                    shape="star"
                                    isAnimationActive={false}
                                />
                            </ScatterChart>
                        </ResponsiveContainer>
                    </div>
                    <div
                        className="flex flex-wrap items-center gap-3 text-[10px] text-ux-text-3"
                        data-testid="pareto-frontier-legend"
                    >
                        <span className="inline-flex items-center gap-1">
                            <span className="w-2 h-2 rounded-full bg-ux-text-3 inline-block opacity-60" />
                            Candidate
                        </span>
                        <span className="inline-flex items-center gap-1">
                            <span className="w-2 h-2 rounded-full bg-ux-accent inline-block" />
                            Pareto frontier
                        </span>
                        <span
                            className="inline-flex items-center gap-1"
                            data-testid="pareto-frontier-knee-marker"
                        >
                            <span
                                className="w-2.5 h-2.5 inline-block bg-ux-text-1 border border-ux-accent"
                                style={{ transform: "rotate(45deg)" }}
                            />
                            Knee point
                        </span>
                        {data.baseline.length > 0 && (
                            <span
                                className="inline-flex items-center gap-1"
                                data-testid="pareto-frontier-baseline-marker"
                            >
                                <span className="text-ux-text-1">★</span>
                                Baseline (Pk {num(baselinePk, 3)})
                            </span>
                        )}
                    </div>
                </>
            )}
        </div>
    );
}
